import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { PageHeader } from "@/components/site/Bits";
import { Reveal } from "@/lib/motion";
import { toast } from "sonner";

const inputCls =
  "w-full border-0 border-b border-aria-travertine bg-transparent py-3 font-body text-lg text-aria-ink placeholder:text-aria-muted/50 focus:border-aria-brass focus:outline-none transition-colors";

const LOCATIONS = [
  { city: "Washington DC", note: "Embassies · Museums · Estates" },
  { city: "New York", note: "Galleries · Rooftops · Galas" },
  { city: "Miami", note: "Waterfront · Brand · Private" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { document.title = "Contact — ARIA"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    try {
      await endpoints.createInquiry({ ...form, event_type: "General Enquiry" });
      setSent(true);
      toast.success("Thank you — your message has reached our team.");
    } catch {
      toast.error("Something went wrong. Please email events@ariadrinks.com.");
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title={["We would love", "to hear from you."]}
        intro="For enquiries, collaborations and press, our team is only a message away."
        testid="contact-header"
      />

      <section className="aria-container pb-28 md:pb-40">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-24">
          <div className="md:col-span-5">
            <div className="eyebrow mb-6">Direct</div>
            <a href="mailto:events@ariadrinks.com" className="aria-link font-display text-3xl md:text-4xl text-aria-ink" data-testid="contact-email">
              events@ariadrinks.com
            </a>

            <div className="mt-14 space-y-8">
              {LOCATIONS.map((l) => (
                <div key={l.city} className="border-t border-aria-travertine pt-5">
                  <div className="font-display text-2xl text-aria-ink">{l.city}</div>
                  <div className="mt-1 text-[0.7rem] uppercase tracking-[0.2em] text-aria-muted">{l.note}</div>
                </div>
              ))}
            </div>

            <Reveal>
              <p className="mt-14 max-w-sm font-light text-aria-cocoa/70">
                Planning a specific occasion? The{" "}
                <Link to="/book" className="aria-link text-aria-ink">enquiry form</Link>{" "}
                lets us prepare a considered response.
              </p>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <div data-testid="contact-success" className="flex h-full min-h-[300px] flex-col justify-center">
                <div className="eyebrow mb-6">Received</div>
                <h2 className="font-display text-4xl md:text-5xl text-aria-ink">Thank you.</h2>
                <p className="mt-6 max-w-md font-light text-lg text-aria-cocoa/80">
                  Your message has reached us. We will write back personally.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
                <label className="block">
                  <span className="mb-2 block text-[0.65rem] uppercase tracking-[0.22em] text-aria-muted">Full Name</span>
                  <input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                </label>
                <label className="block">
                  <span className="mb-2 block text-[0.65rem] uppercase tracking-[0.22em] text-aria-muted">Email</span>
                  <input data-testid="contact-input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </label>
                <label className="block">
                  <span className="mb-2 block text-[0.65rem] uppercase tracking-[0.22em] text-aria-muted">Message</span>
                  <textarea data-testid="contact-notes" rows={5} className={`${inputCls} resize-none`} value={form.notes} onChange={set("notes")} placeholder="How may we help?" />
                </label>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-4 bg-aria-ink px-10 py-5 text-[0.72rem] uppercase tracking-[0.24em] text-aria-ivory transition-colors hover:bg-aria-cocoa"
                >
                  Send Message <span aria-hidden>→</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
